import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { ArrowLeft, Pencil, Database, Lightbulb, Maximize, Minimize, Maximize2, X } from 'lucide-react';
import { apiFetch } from '../../api';
import { useAuth } from '../../context/useAuth';
import { WidgetRenderer } from '../../components/DashboardStudio/WidgetRenderer';
import { useTimezoneFormatter } from '../../hooks/useTimezoneFormatter';
import { isTenantScoped } from '../../utils/permissions';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

/**
 * Optimized View Mode for Dashboards
 * - Lightweight: No dataset dropdown, no toolbox, no editing
 * - Fast: Loads only datasets used by widgets
 * - Read-only: Pure data visualization
 * - Responsive: Matches Editor layout accurately
 */
interface DashboardViewPageProps {
    dashboardId?: string;
    onBack?: () => void;
    onEdit?: (id?: string) => void;
    embedded?: boolean;
}

const DashboardViewPage = ({ dashboardId: propId, onBack, onEdit, embedded = false }: DashboardViewPageProps) => {
    const params = useParams();
    const dashboardId = propId || params.id;
    const navigate = useNavigate();
    const { token, user } = useAuth();
    const { formatDate } = useTimezoneFormatter();

    // State
    const [dashboard, setDashboard] = useState<any>(null);
    const [widgetItems, setWidgetItems] = useState<any[]>([]);
    const [widgetDatasets, setWidgetDatasets] = useState<Record<string, any>>({});
    const [selectedDataModel, setSelectedDataModel] = useState<any>(null);
    const [customMeasures, setCustomMeasures] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeInsightWidgetId, setActiveInsightWidgetId] = useState<string | null>(null);
    const [globalFilters, setGlobalFilters] = useState<Record<string, any>>({});
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [expandedWidgetId, setExpandedWidgetId] = useState<string | null>(null);

    // Escape key handler to close expanded widget
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && expandedWidgetId) {
                setExpandedWidgetId(null);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [expandedWidgetId]);

    // Phase 1: data source badge
    const [dataSourceStatus, setDataSourceStatus] = useState<{ source: 'platform' | 'tenant'; status: string; last_refreshed_at: string | null } | null>(null);
    // Gate: block chart queries until auto-selected filters are resolved
    const [filtersReady, setFiltersReady] = useState(false);
    const pendingLatestRef = React.useRef(0);

    // Full Screen Toggle Logic (CSS Overlay)
    const toggleFullScreen = () => {
        setIsFullScreen(!isFullScreen);
    };

    // Auto-hide controls in full screen
    const [showControls, setShowControls] = useState(true);
    const controlsTimeoutRef = React.useRef<any>(null);

    const handleMouseMove = () => {
        setShowControls(true);
        if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
        if (isFullScreen) {
            controlsTimeoutRef.current = setTimeout(() => {
                setShowControls(false);
            }, 3000);
        }
    };

    useEffect(() => {
        return () => {
            if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
        };
    }, []);

    const handleFilterChange = (field: string, value: any) => {
        setGlobalFilters(prev => {
            const newFilters = { ...prev };
            if (value === null || value === undefined || value === '') {
                delete newFilters[field];
            } else {
                newFilters[field] = value;
            }
            return newFilters;
        });
    };

    // Called by FilterWidget when a useLatest auto-selection fires
    const handleFiltersResolved = () => {
        pendingLatestRef.current = Math.max(0, pendingLatestRef.current - 1);
        if (pendingLatestRef.current === 0) {
            setFiltersReady(true);
        }
    };

    // DEBUG LOGS
    // console.log('[DashboardViewPage] MOUNTED. ID:', dashboardId, '{Prop:', propId, 'Params:', params.id, '}');

    // Handle Back and Edit
    const handleBack = () => {
        if (onBack) {
            onBack();
        } else {
            navigate('/dashboards');
        }
    };

    const handleEdit = () => {
        if (onEdit) {
            onEdit(dashboardId); // Pass ID to callback
        } else {
            navigate(`/dashboards/${dashboardId}/edit`);
        }
    };

    // === Load Dashboard ===
    useEffect(() => {
        const controller = new AbortController();

        const loadDashboard = async () => {
            try {
                setLoading(true);
                // 1. Fetch Dashboard Metadata
                const dash = await apiFetch(`/dashboards/${dashboardId}`, token, { signal: controller.signal });

                if (!dash) {
                    if (!controller.signal.aborted) setError('Dashboard not found');
                    return;
                }

                if (controller.signal.aborted) return;
                setDashboard(dash);

                // Parse widgets
                const widgets = Object.entries(dash.widgets || {}).map(([id, config]: [string, any]) => ({
                    id,
                    type: config.type || 'bar',
                    title: config.title,
                    datasetId: config.dataset_id,
                    dashboardId: dashboardId, // Pass ID for widget context
                    config: config.config || {},
                    layout: config.layout || dash.layout_config?.find((l: any) => l.i === id)
                }));

                setWidgetItems(widgets);

                // --- INITIALIZE DEFAULT FILTERS ---
                const initialFilters: Record<string, any> = {};
                widgets.forEach(w => {
                    if (w.type === 'filter' && w.config?.column) {
                        w.config.column.forEach((col: any) => {
                            if (col.defaultValue !== undefined && col.defaultValue !== null && col.defaultValue !== '') {
                                initialFilters[col.name] = col.defaultValue;
                            }
                        });
                    }
                });
                if (Object.keys(initialFilters).length > 0) {
                    // console.log('[DashboardViewPage] Initializing Default Filters:', initialFilters);
                    setGlobalFilters(initialFilters);
                }

                // Count how many filter fields have useLatest=true (require async resolution)
                let latestCount = 0;
                widgets.forEach(w => {
                    if (w.type === 'filter' && w.config?.column) {
                        w.config.column.forEach((col: any) => {
                            if (col.useLatest) latestCount++;
                        });
                    }
                });
                pendingLatestRef.current = latestCount;
                // If no useLatest filters, charts can load immediately
                setFiltersReady(latestCount === 0);

                // OPTIMIZATION: Removed Metadata Waterfall
                // We don't need to fetch full dataset metadata for View Mode.
                // The widgets only need the ID to query the backend.

                // Build widgetId -> dataset map (Mock object with ID)
                const datasetsMap = {};
                widgets.forEach(w => {
                    if (w.datasetId) {
                        datasetsMap[w.id] = { id: w.datasetId };
                    }
                });

                // console.log('[DashboardViewPage] Data Loaded (Fast Mode). Widgets:', widgets.length);

                setWidgetDatasets(datasetsMap);

                // --- LOAD DATA MODEL + CUSTOM MEASURES ---
                const loadedConfig = dash.theme_config || dash.config || {};
                const loadedMeasures = dash.customMeasures || dash.custom_measures || loadedConfig.customMeasures;
                if (loadedMeasures && loadedMeasures.length > 0) {
                    setCustomMeasures(loadedMeasures);
                }

                const dataModelId = loadedConfig.dataModelId;

                if (dataModelId) {
                    apiFetch(`/datamodels/${dataModelId}`, token)
                        .then((dm: any) => setSelectedDataModel(dm))
                        .catch(() => {
                            console.warn('[DashboardViewPage] DataModel fetch failed, using ID-only proxy:', dataModelId);
                            setSelectedDataModel({ id: dataModelId });
                        });

                    // Phase 1: fetch data source status for the badge
                    apiFetch(`/dashboards/data-status?model_ids=${dataModelId}`, token)
                        .then((statusMap: any) => {
                            const info = statusMap[dataModelId];
                            if (info) setDataSourceStatus(info);
                        })
                        .catch(() => { /* non-fatal */ });
                }


                setLoading(false);
            } catch (err: any) {
                if (err.name === 'AbortError') return;
                console.error('Failed to load dashboard:', err);
                setError(err.message || 'Failed to load dashboard');
                setLoading(false);
            }
        };

        if (dashboardId) {
            loadDashboard();
        }

        return () => {
            controller.abort();
        };
    }, [dashboardId, token]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-slate-50 dark:bg-slate-900">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-slate-600 dark:text-slate-400">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-screen bg-slate-50 dark:bg-slate-900">
                <div className="text-center">
                    <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
                    <button
                        onClick={handleBack}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        Back to Dashboards
                    </button>
                </div>
            </div>
        );
    }

    // Grid Config (Match Editor for Consistency)
    const colsMap = { lg: 120, md: 120, sm: 120, xs: 60, xxs: 40 };

    return (
        <div
            className={`flex flex-col bg-slate-50 dark:bg-slate-900 overflow-hidden font-sans text-slate-900 dark:text-slate-100 transition-all duration-300 ${isFullScreen ? 'fixed inset-0 z-[9999] h-screen w-screen' : 'h-full w-full relative'}`}
            onMouseMove={handleMouseMove}
        >
            {/* Header - Simplist View Mode */}
            {!embedded && (
                <div className={`
                bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 
                px-4 py-3 flex items-center justify-between
                transition-all duration-300 ease-in-out
                ${isFullScreen ? (showControls ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full absolute top-0 w-full z-10 pointer-events-none') : ''}
                ${dashboard?.theme_config?.canvas?.backgroundColor ? '' : ''} 
            `}>
                    <div className="flex items-center gap-3">
                        {/* Back Button - Always Visible */}
                        <button
                            onClick={handleBack}
                            className="p-2 -ml-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 transition-colors"
                            title="Back to Dashboards"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>

                        {/* Conditional Title */}
                        {(dashboard?.theme_config?.showTitle !== false) && (
                            <div>
                                <h1
                                    className="text-xl font-bold text-slate-900 dark:text-slate-100 truncate max-w-xl"
                                    style={dashboard?.theme_config?.titleStyle ? {
                                        color: dashboard.theme_config.titleStyle.color,
                                        fontSize: dashboard.theme_config.titleStyle.fontSize,
                                        fontWeight: dashboard.theme_config.titleStyle.bold ? 'bold' : 'normal',
                                        fontStyle: dashboard.theme_config.titleStyle.italic ? 'italic' : 'normal',
                                        textDecoration: dashboard.theme_config.titleStyle.underline ? 'underline' : 'none',
                                    } : {}}
                                >
                                    {dashboard?.name || 'Dashboard'}
                                </h1>
                            </div>
                        )}
                    </div>

                    {/* Right Actions */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={toggleFullScreen}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                            title={isFullScreen ? "Exit Full Screen" : "Enter Full Screen"}
                        >
                            {isFullScreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
                            <span className="hidden sm:inline">{isFullScreen ? 'Exit Full Screen' : 'Full Screen'}</span>
                        </button>

                        {onEdit && (
                            <button
                                onClick={handleEdit}
                                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                title="Edit Dashboard"
                            >
                                <Pencil className="w-4 h-4" />
                                <span className="hidden sm:inline">Edit</span>
                            </button>
                        )}

                    </div>
                </div>
            )}

            {/* Data Source Banner — only shown when viewing platform sample data (not tenant's own dashboards) */}
            {dataSourceStatus?.source === 'platform' && isTenantScoped(user) && (
                <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 text-xs">
                    <Database className="w-3.5 h-3.5 shrink-0" />
                    <span>
                        <strong>Showing sample data.</strong>{' '}
                        Connect your own data source to see your organization's live data.
                    </span>
                </div>
            )}

            {/* Dashboard Grid */}
            <div
                className="flex-1 overflow-auto"
                style={{
                    backgroundColor: dashboard?.theme_config?.canvas?.backgroundColor || 'transparent',
                    padding: dashboard?.theme_config?.canvas?.margin !== undefined
                        ? dashboard.theme_config.canvas.margin
                        : '16px', // Match edit mode default (16px, not 24px/p-6)
                    paddingBottom: '2rem'
                }}
            >
                <ResponsiveGridLayout
                    className="layout"
                    layouts={{ lg: dashboard?.layout_config || [], md: dashboard?.layout_config || [] }}
                    breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                    cols={colsMap}
                    rowHeight={5}
                    isDraggable={false}
                    isResizable={false}
                    margin={[5, 5]}
                    useCSSTransforms={true}
                >
                    {widgetItems.map((widget) => (
                        <div
                            key={widget.id}
                            data-grid={widget.layout}
                            className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col relative group"
                        >
                            {/* FLOATING ACTION OVERLAY (Top-Right) */}
                            {/* Always presence for layout stability, but content fades in/out */}
                            <div className={`
                                absolute top-2 right-2 z-50 flex items-center gap-1 
                                transition-all duration-200 ease-in-out
                                ${widget.id === activeInsightWidgetId ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 group-hover:opacity-100 group-hover:translate-y-0'}
                            `}>
                                {/* AI Insights Trigger */}
                                {widget.config?.aiInsights && (
                                    <div className="flex items-center gap-1 p-1 rounded-lg bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm shadow-sm border border-slate-200 dark:border-slate-700">
                                        <button
                                            className={`p-1.5 rounded-md transition-colors ${widget.id === activeInsightWidgetId ? 'text-amber-500 bg-amber-50 dark:bg-amber-900/30' : 'text-slate-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/30'}`}
                                            title="Toggle AI Insights"
                                            onClick={(e) => { e.stopPropagation(); setActiveInsightWidgetId(prev => prev === widget.id ? null : widget.id); }}
                                        >
                                            <Lightbulb className="w-3.5 h-3.5" fill={widget.id === activeInsightWidgetId ? "currentColor" : "none"} />
                                        </button>
                                    </div>
                                )}
                            </div>
                            {widget.config?.style?.showTitle !== false && (
                                <div className="h-8 flex items-center justify-between px-3 bg-slate-50/50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-700">
                                    <span
                                        className="truncate flex items-center gap-2"
                                        style={{
                                            color: widget.config?.style?.color,
                                            fontSize: widget.config?.style?.fontSize ? `${widget.config.style.fontSize}px` : '10px',
                                            fontWeight: widget.config?.style?.fontWeight || 'bold',
                                            fontStyle: widget.config?.style?.fontStyle || 'normal',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.1em'
                                        }}
                                    >

                                        {widget.config?.title || widget.title || `${widget.type} Widget`}
                                    </span>

                                    {/* Header Actions */}
                                    <div className="flexitems-center gap-1">
                                        <button
                                            className="p-1 rounded text-slate-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                                            title="Expand Widget"
                                            onClick={(e) => { e.stopPropagation(); setExpandedWidgetId(widget.id); }}
                                        >
                                            <Maximize2 className="w-3.5 h-3.5" />
                                        </button>
                                        
                                        {widget.config?.aiInsights && (
                                            <button
                                                className={`p-1 rounded transition-colors ${widget.id === activeInsightWidgetId ? 'text-amber-500 bg-amber-50' : 'text-slate-400 hover:text-amber-500 hover:bg-amber-50'}`}
                                                title="Toggle AI Insights"
                                                onClick={(e) => { e.stopPropagation(); setActiveInsightWidgetId(prev => prev === widget.id ? null : widget.id); }}
                                            >
                                                <Lightbulb className="w-3.5 h-3.5" fill={widget.id === activeInsightWidgetId ? "currentColor" : "none"} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="flex-1 overflow-hidden relative">
                                <WidgetRenderer
                                    widget={widget}
                                    dataset={widgetDatasets[widget.id]}
                                    dataModel={selectedDataModel}
                                    customMeasures={customMeasures}
                                    showInsights={widget.id === activeInsightWidgetId}
                                    onToggleInsights={() => setActiveInsightWidgetId(prev => prev === widget.id ? null : widget.id)}
                                    globalFilters={globalFilters}
                                    onFilterChange={handleFilterChange}
                                    onFiltersResolved={handleFiltersResolved}
                                    filtersReady={filtersReady}
                                    onUpdate={() => { }} // Read-only
                                />
                            </div>
                        </div>
                    ))}
                </ResponsiveGridLayout>
            </div>

            {/* EXPANDED WIDGET MODAL */}
            {expandedWidgetId && (
                <div className="absolute inset-0 z-[100] bg-slate-900/80 backdrop-blur-sm p-4 sm:p-8 flex flex-col">
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl flex flex-col w-full h-full overflow-hidden border border-slate-200 dark:border-slate-700 animate-in fade-in zoom-in-95 duration-200">
                        {(() => {
                            const expandedWidget = widgetItems.find(w => w.id === expandedWidgetId);
                            if (!expandedWidget) return null;
                            return (
                                <>
                                    <div className="h-12 flex items-center justify-between px-4 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 shrink-0">
                                        <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200 truncate">
                                            {expandedWidget.config?.title || expandedWidget.title || `${expandedWidget.type} Widget`}
                                        </h2>
                                        <div className="flex items-center gap-2">
                                            {expandedWidget.config?.aiInsights && (
                                                <button
                                                    className={`p-1.5 rounded-lg transition-colors ${expandedWidget.id === activeInsightWidgetId ? 'text-amber-500 bg-amber-50 dark:bg-amber-900/30' : 'text-slate-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-slate-800'}`}
                                                    title="Toggle AI Insights"
                                                    onClick={(e) => { e.stopPropagation(); setActiveInsightWidgetId(prev => prev === expandedWidget.id ? null : expandedWidget.id); }}
                                                >
                                                    <Lightbulb className="w-5 h-5" fill={expandedWidget.id === activeInsightWidgetId ? "currentColor" : "none"} />
                                                </button>
                                            )}
                                            <button
                                                className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                                                onClick={() => setExpandedWidgetId(null)}
                                            >
                                                <X className="w-6 h-6" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex-1 overflow-hidden relative bg-white dark:bg-slate-800 p-2">
                                        <WidgetRenderer
                                            widget={expandedWidget}
                                            dataset={widgetDatasets[expandedWidget.id]}
                                            dataModel={selectedDataModel}
                                            customMeasures={customMeasures}
                                            showInsights={expandedWidget.id === activeInsightWidgetId}
                                            onToggleInsights={() => setActiveInsightWidgetId(prev => prev === expandedWidget.id ? null : expandedWidget.id)}
                                            globalFilters={globalFilters}
                                            onFilterChange={handleFilterChange}
                                            onFiltersResolved={handleFiltersResolved}
                                            filtersReady={filtersReady}
                                            onUpdate={() => { }}
                                        />
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DashboardViewPage;
